/*
==========================================
STAMP PHOTO EDITOR  v1.0.1
文件: assets/stamp-photo-editor.js

v1.0.1 改动：
- [FIX] _bindEvents() 新增 userSelect:none / webkitUserSelect:none
  确保 touchmove 不被浏览器文字选择打断

v1.0.0（保留）：
- 通用照片 Move / Erase / Reset 模块
由 stamp-address-composer.js（产品8）
和 stamp-body-composer.js（产品7）共同调用

职责：
  - 管理 _headCanvas（离屏 Canvas，存储照片像素）
  - 提供 Move（拖拽重定位）/ Erase（橡皮擦）工具
  - 提供 Reset（还原原始照片，保留位移/缩放）
  - 绑定鼠标 + 触控事件到指定的输出 Canvas
  - 通过 onRender 回调通知宿主重绘

不依赖任何产品特定逻辑，宿主只需提供：
  1. outputCanvas      —— 实际显示的 <canvas> 元素
  2. getHeadRect()     —— 返回照片在输出 canvas 坐标系中的绘制区域
                          { x, y, w, h }（照片中心和尺寸）
  3. onRender()        —— 当 _headCanvas 内容变化后调用，宿主负责重绘整个 canvas

API:
  StampPhotoEditor.create(options) → instance
    options:
      canvas       <HTMLCanvasElement>  输出 canvas
      getHeadRect  Function             返回 { cx, cy, diam } 或 { x, y, w, h }
      onRender     Function             重绘回调
      outputW      Number               canvas 逻辑宽（默认同 canvas.width）
      outputH      Number               canvas 逻辑高（默认同 canvas.height）

  instance 方法：
    setHead(dataUrl)        —— 加载新照片，重置位移
    getHeadCanvas()         —— 返回当前 _headCanvas（宿主用于绘制）
    getOffset()             —— 返回 { x, y }（宿主用于定位）
    getScale()              —— 返回当前缩放比
    setOffset(x, y)         —— 直接设定位移
    setScale(scale)         —— 设定缩放（0.3 ~ 3.0）
    setTool(mode)           —— 'move' | 'eraser'
    setBrushSize(px)        —— 橡皮擦笔刷大小（逻辑像素）
    resetEraser()           —— 还原照片像素，保留位移/缩放
    destroy()               —— 解绑所有事件监听器

!! 不要在这个文件里写产品特定代码 !!
==========================================
*/

window.StampPhotoEditor = (function () {
  'use strict';

  // ── 工厂函数，每次调用返回一个独立实例 ──
  function create(options) {
    const {
      canvas,
      getHeadRect,   // Function → { cx, cy, diam }  OR  { x, y, w, h }
      onRender,
      outputW: _ow,
      outputH: _oh
    } = options;

    if (!canvas || typeof getHeadRect !== 'function' || typeof onRender !== 'function') {
      console.error('[StampPhotoEditor] Missing required options: canvas, getHeadRect, onRender');
      return null;
    }

    // ── 状态 ──
    let _headCanvas    = null;
    let _originalImg   = null;  // 原始 HTMLImageElement，用于 reset
    let _headOffsetX   = 0;
    let _headOffsetY   = 0;
    let _headScale     = 1.0;

    let _toolMode      = 'move';   // 'move' | 'eraser'
    let _brushSize     = 20;
    let _isDrawing     = false;
    let _lastX         = 0, _lastY = 0;

    let _dragging      = false;
    let _dragStartX    = 0, _dragStartY  = 0;
    let _dragStartOX   = 0, _dragStartOY = 0;

    // ── 坐标换算（逻辑坐标 = 与 outputW/outputH 对应的坐标系）──
    const outputW = () => _ow || canvas.width;
    const outputH = () => _oh || canvas.height;

    function _getPos(e) {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return {
        x: (t.clientX - rect.left) * (outputW() / rect.width),
        y: (t.clientY - rect.top)  * (outputH() / rect.height)
      };
    }

    // getHeadRect 返回宿主当前的照片绘制参数
    // 统一转换为 { hx, hy, diam } —— 照片左上角及直径（正方形照片区域）
    function _getDrawRect() {
      const r = getHeadRect();
      // 支持两种格式
      if (r.cx !== undefined) {
        // { cx, cy, diam }
        return { hx: r.cx - r.diam / 2 + _headOffsetX, hy: r.cy - r.diam / 2 + _headOffsetY, diam: r.diam };
      } else {
        // { x, y, w, h } —— 取中心、取最小边作为直径
        const diam = Math.min(r.w, r.h) * _headScale;
        const cx = r.x + r.w / 2;
        const cy = r.y + r.h / 2;
        return { hx: cx - diam / 2 + _headOffsetX, hy: cy - diam / 2 + _headOffsetY, diam };
      }
    }

    // ── 核心：橡皮擦 ──
    function _erase(cx, cy) {
      if (!_headCanvas) return;
      const { hx, hy, diam } = _getDrawRect();
      const sx = _headCanvas.width  / diam;
      const sy = _headCanvas.height / diam;
      const lr = (_brushSize / 2) * Math.max(sx, sy);
      const hctx = _headCanvas.getContext('2d');
      hctx.save();
      hctx.globalCompositeOperation = 'destination-out';
      hctx.beginPath();
      hctx.arc((cx - hx) * sx, (cy - hy) * sy, lr, 0, Math.PI * 2);
      hctx.fill();
      hctx.restore();
      onRender();
    }

    function _interpolate(x1, y1, x2, y2) {
      const dist  = Math.hypot(x2 - x1, y2 - y1);
      const steps = Math.max(1, Math.ceil(dist / (_brushSize * 0.25)));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        _erase(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t);
      }
    }

    // ── 事件绑定 ──
    // 采用与 stamp-body-composer.js v1.3.3 相同的策略：
    // move 模式下 touchstart 用 passive:true + 直接 return，
    // erase 模式用 passive:false + preventDefault，两者互不干扰
    const _listeners = [];  // 用于 destroy()

    function _on(el, type, handler, opts) {
      el.addEventListener(type, handler, opts);
      _listeners.push({ el, type, handler, opts });
    }

    function _bindEvents() {
      // touch-action:none 让浏览器把所有触摸事件交给 JS 处理
      canvas.style.touchAction = 'none';
      canvas.style.userSelect  = 'none';
      canvas.style.webkitUserSelect = 'none';

      // ── mousedown ──
      const onMouseDown = (e) => {
        e.preventDefault();
        const pos = _getPos(e);
        if (_toolMode === 'eraser') {
          _isDrawing = true; _lastX = pos.x; _lastY = pos.y; _erase(pos.x, pos.y);
        } else {
          _dragging = true;
          _dragStartX = pos.x; _dragStartY = pos.y;
          _dragStartOX = _headOffsetX; _dragStartOY = _headOffsetY;
          canvas.style.cursor = 'grabbing';
        }
      };
      _on(canvas, 'mousedown', onMouseDown);

      // ── mousemove（document 级，支持拖出 canvas）──
      const onMouseMove = (e) => {
        if (!_isDrawing && !_dragging) return;
        const pos = _getPos(e);
        if (_toolMode === 'eraser' && _isDrawing) {
          _interpolate(_lastX, _lastY, pos.x, pos.y);
          _lastX = pos.x; _lastY = pos.y;
        } else if (_toolMode === 'move' && _dragging) {
          _headOffsetX = _dragStartOX + (pos.x - _dragStartX);
          _headOffsetY = _dragStartOY + (pos.y - _dragStartY);
          onRender();
        }
      };
      _on(document, 'mousemove', onMouseMove);

      // ── mouseup ──
      const onMouseUp = () => {
        _isDrawing = false; _dragging = false;
        canvas.style.cursor = _toolMode === 'move' ? 'grab' : 'crosshair';
      };
      _on(document, 'mouseup', onMouseUp);

      // ── touchstart（move 模式：passive，不阻止；erase 模式：non-passive，阻止滚动）──
      _on(canvas, 'touchstart', (e) => {
        if (_toolMode === 'move') {
          // passive 路径：直接处理拖拽，不 preventDefault
          const pos = _getPos(e);
          _dragging = true;
          _dragStartX = pos.x; _dragStartY = pos.y;
          _dragStartOX = _headOffsetX; _dragStartOY = _headOffsetY;
          return;
        }
        // erase 模式（此监听器 passive:true，无法 preventDefault，
        // 由下面的 non-passive 监听器处理）
      }, { passive: true });

      // erase 模式专用 non-passive touchstart
      _on(canvas, 'touchstart', (e) => {
        if (_toolMode !== 'eraser') return;
        e.preventDefault();
        _isDrawing = true;
        const pos = _getPos(e); _lastX = pos.x; _lastY = pos.y; _erase(pos.x, pos.y);
      }, { passive: false });

      // ── touchmove ──
      _on(canvas, 'touchmove', (e) => {
        if (_toolMode === 'eraser' && _isDrawing) {
          e.preventDefault();
          const pos = _getPos(e);
          _interpolate(_lastX, _lastY, pos.x, pos.y);
          _lastX = pos.x; _lastY = pos.y;
        } else if (_toolMode === 'move' && _dragging) {
          const pos = _getPos(e);
          const dx = pos.x - _dragStartX, dy = pos.y - _dragStartY;
          // 竖向滑动超过横向时让浏览器滚动
          if (Math.abs(dy) > Math.abs(dx) + 5) { _dragging = false; return; }
          e.preventDefault();
          _headOffsetX = _dragStartOX + dx;
          _headOffsetY = _dragStartOY + dy;
          onRender();
        }
      }, { passive: false });

      // ── touchend ──
      _on(document, 'touchend', () => { _isDrawing = false; _dragging = false; });
      _on(document, 'touchcancel', () => { _isDrawing = false; _dragging = false; });
    }

    // ── 工具函数 ──
    function _loadImg(url) {
      return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload  = () => resolve(img);
        img.onerror = () => { console.error('[StampPhotoEditor] Failed to load image'); resolve(null); };
        img.src = url;
      });
    }

    // ── 初始化事件 ──
    _bindEvents();
    canvas.style.cursor = 'grab';

    // ── 公开 API ──
    return {
      // 加载新照片
      async setHead(dataUrl) {
        const img = await _loadImg(dataUrl);
        if (!img) return;
        _originalImg   = img;
        _headCanvas    = document.createElement('canvas');
        _headCanvas.width  = img.width;
        _headCanvas.height = img.height;
        _headCanvas.getContext('2d').drawImage(img, 0, 0);
        _headOffsetX = 0; _headOffsetY = 0;
        onRender();
      },

      // 还原橡皮擦，保留位移和缩放
      async resetEraser() {
        if (!_originalImg) return;
        const ox = _headOffsetX, oy = _headOffsetY, os = _headScale;
        _headCanvas = document.createElement('canvas');
        _headCanvas.width  = _originalImg.width;
        _headCanvas.height = _originalImg.height;
        _headCanvas.getContext('2d').drawImage(_originalImg, 0, 0);
        _headOffsetX = ox; _headOffsetY = oy; _headScale = os;
        onRender();
      },

      // 获取宿主绘制时需要的信息
      getHeadCanvas() { return _headCanvas; },
      getOffset()     { return { x: _headOffsetX, y: _headOffsetY }; },
      getScale()      { return _headScale; },

      // 设定位移 / 缩放
      setOffset(x, y) { _headOffsetX = x; _headOffsetY = y; onRender(); },
      setScale(scale) {
        _headScale = Math.max(0.3, Math.min(3.0, scale));
        onRender();
      },

      // 工具切换
      setTool(mode) {
        _toolMode = mode;
        canvas.style.cursor = mode === 'move' ? 'grab' : 'crosshair';
      },

      // 兼容旧接口
      setEraserActive(on) { this.setTool(on ? 'eraser' : 'move'); },
      setBrushSize(px)    { _brushSize = px; },
      setEraserSize(px)   { _brushSize = px; },

      // 解绑所有事件（切换产品/组件卸载时调用）
      destroy() {
        _listeners.forEach(({ el, type, handler, opts }) => {
          el.removeEventListener(type, handler, opts);
        });
        _listeners.length = 0;
      }
    };
  }

  return { create };

})();